import { useEffect, useRef, useState } from "react";
import { Phone, Trees, Truck, Sparkles, Home, UtensilsCrossed, X } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";

const DEMOS = [
  {
    key: "plumbing",
    business: "Twin City Plumbing",
    industry: "Plumbing",
    tagline: "Fast. Reliable. Professional.",
    image: "/generated/plumbing_hero.png",
    Icon: Phone,
    theme: {
      accent: "#1D4ED8",
      accentHover: "#1E40AF",
      soft: "bg-blue-50",
      chip: "bg-blue-600/10 text-blue-700",
    },
    services: [
      "Emergency Repairs",
      "Drain Cleaning",
      "Water Heaters",
      "Bathroom Remodels",
    ],
    tiles: ["Licensed & Insured", "24/7 Emergency", "Upfront Pricing"],
  },
  {
    key: "landscaping",
    business: "Greenline Landscaping",
    industry: "Landscaping",
    tagline: "Beautiful Lawns. Happy Homes.",
    image: "/generated/landscaping_hero.png",
    Icon: Trees,
    theme: {
      accent: "#15803D",
      accentHover: "#166534",
      soft: "bg-emerald-50",
      chip: "bg-emerald-600/10 text-emerald-700",
    },
    services: [
      "Lawn Care",
      "Landscape Design",
      "Hedge Trimming",
      "Seasonal Cleanup",
    ],
    tiles: ["Free Estimates", "Weekly Service", "Locally Owned"],
  },
  {
    key: "truck",
    business: "Highway Diesel Repair",
    industry: "Truck & Diesel Repair",
    tagline: "Keep Your Trucks Moving",
    image: "/generated/truck_repair_hero.png",
    Icon: Truck,
    theme: {
      accent: "#B91C1C",
      accentHover: "#991B1B",
      soft: "bg-neutral-900",
      chip: "bg-red-600/10 text-red-700",
    },
    dark: true,
    services: [
      "Diesel Engine Repair",
      "Roadside Assistance",
      "Fleet Maintenance",
      "DOT Inspections",
    ],
    tiles: ["Certified Mechanics", "Fast Turnaround", "Fleet Discounts"],
  },
  {
    key: "cleaning",
    business: "Bright Space Cleaning",
    industry: "Cleaning / Local Business",
    tagline: "A Cleaner Space. A Better Place.",
    image: "/generated/cleaning_hero.png",
    Icon: Sparkles,
    theme: {
      accent: "#0F766E",
      accentHover: "#115E59",
      soft: "bg-teal-50",
      chip: "bg-teal-600/10 text-teal-700",
    },
    services: [
      "Residential Cleaning",
      "Office Cleaning",
      "Deep Cleans",
      "Move-In / Move-Out",
    ],
    tiles: ["Vetted Cleaners", "Flexible Scheduling", "Satisfaction Guaranteed"],
  },
  {
    key: "roofing",
    business: "Summit Roofing",
    industry: "Roofing",
    tagline: "Protecting Your Home From the Top Down",
    // Reuses the hero-mockup house image already in /public/generated/
    image: "/generated/midwest_roofing_house.png",
    Icon: Home,
    theme: {
      // Navy/slate panel with a warm amber accent — visually distinct
      // from the dark/red truck-repair card.
      accent: "#B45309",
      accentHover: "#92400E",
      soft: "bg-slate-50",
      chip: "bg-amber-600/10 text-amber-700",
    },
    ctaLabel: "Get a Free Quote",
    services: [
      "Roof Repair",
      "Roof Replacement",
      "Storm Damage",
      "Gutter Services",
    ],
    tiles: ["Licensed & Insured", "Free Inspections", "Local & Trusted"],
  },
  {
    key: "restaurant",
    business: "Oak & Table",
    industry: "Restaurant / Local Business",
    tagline: "Local Flavor. Made Fresh.",
    image: "/generated/restaurant_hero.png",
    Icon: UtensilsCrossed,
    theme: {
      // Warm neutrals: cream card + deep green accent
      accent: "#166534",
      accentHover: "#14532D",
      soft: "bg-amber-50",
      chip: "bg-emerald-700/10 text-emerald-800",
    },
    ctaLabel: "View Menu",
    services: [
      "Seasonal Menu",
      "Private Dining",
      "Weekend Brunch",
      "Catering",
    ],
    tiles: ["Locally Sourced", "Family Owned", "Craft Beverages"],
  },
];

const BrowserChrome = ({ url }) => (
  <div className="flex items-center gap-1.5 bg-goc-light px-3 py-2 border-b border-goc-border">
    <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
    <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
    <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
    <div className="ml-2 flex-1 truncate text-[10px] text-goc-muted">{url}</div>
  </div>
);

const DemoCard = ({ demo, onOpen }) => {
  const { business, tagline, image, Icon, theme, industry, ctaLabel } = demo;
  const cardCta = ctaLabel || "Get a Quote";
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group relative text-left bg-white border border-goc-border rounded-2xl overflow-hidden shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-[box-shadow,transform] w-full"
      data-testid={`demo-card-${demo.key}`}
      aria-label={`Open ${industry} demo`}
    >
      <span className="absolute top-3 right-3 z-10 inline-flex items-center rounded-full bg-goc-text/90 text-white text-[10px] font-semibold px-2.5 py-1 tracking-wide">
        Demo
      </span>
      <BrowserChrome url={`${demo.key}.example`} />
      <div className="relative aspect-[16/10] overflow-hidden">
        <img
          src={image}
          alt={`${industry} example website hero`}
          loading="lazy"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-transparent" />
        <div className="absolute inset-0 p-4 flex flex-col justify-between">
          <div className="flex items-center gap-2">
            <span
              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-white"
              style={{ backgroundColor: theme.accent }}
            >
              <Icon className="h-3.5 w-3.5" strokeWidth={2.5} />
            </span>
            <span className="text-white/95 text-xs font-bold">{business}</span>
          </div>
          <div>
            <h3 className="text-white text-lg md:text-xl font-bold leading-tight">
              {tagline}
            </h3>
            <span
              className="mt-2 inline-flex items-center rounded-md px-3 py-1.5 text-xs font-semibold text-white"
              style={{ backgroundColor: theme.accent }}
            >
              {cardCta}
            </span>
          </div>
        </div>
      </div>
      <div className="p-4">
        <p className="text-xs font-semibold uppercase tracking-widest text-goc-muted">
          {industry}
        </p>
        <p className="mt-1 text-sm text-goc-text font-medium">
          Click to preview →
        </p>
      </div>
    </button>
  );
};

const DemoModal = ({ demo, onClose }) => {
  const dialogRef = useRef(null);
  const closeBtnRef = useRef(null);

  useEffect(() => {
    if (!demo) return;
    const previouslyFocused = document.activeElement;
    closeBtnRef.current?.focus();
    document.body.style.overflow = "hidden";

    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      } else if (e.key === "Tab") {
        // basic focus trap
        const focusables = dialogRef.current?.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (!focusables || focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      if (previouslyFocused && previouslyFocused.focus) {
        previouslyFocused.focus();
      }
    };
  }, [demo, onClose]);

  if (!demo) return null;
  const { business, tagline, image, Icon, theme, services, tiles, industry, ctaLabel } =
    demo;
  const primaryCta = ctaLabel || "Get a Free Quote";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-6"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="absolute inset-0 bg-goc-navy/60 backdrop-blur-sm"
        aria-hidden="true"
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="demo-modal-title"
        className="relative w-full max-w-4xl bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto animate-fade-in-up"
        onClick={(e) => e.stopPropagation()}
        data-testid={`demo-modal-${demo.key}`}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-goc-border bg-white sticky top-0 z-10">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center rounded-full bg-goc-text text-white text-[10px] font-semibold px-2.5 py-1 tracking-wide">
              Demo — not a real customer
            </span>
            <span className="hidden sm:inline text-xs text-goc-muted">
              {industry}
            </span>
          </div>
          <button
            ref={closeBtnRef}
            type="button"
            onClick={onClose}
            aria-label="Close demo preview"
            className="inline-flex h-9 w-9 items-center justify-center rounded-md text-goc-muted hover:text-goc-text hover:bg-goc-light transition-colors"
            data-testid={`demo-modal-close-${demo.key}`}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 md:p-8">
          <div className="rounded-xl overflow-hidden border border-goc-border">
            <BrowserChrome url={`${demo.key}.example`} />
            <div className="relative aspect-[16/9]">
              <img
                src={image}
                alt={`${industry} example website hero`}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/35 to-transparent" />
              <div className="absolute inset-0 p-6 md:p-8 flex flex-col justify-between">
                <div className="flex items-center gap-2.5">
                  <span
                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-white"
                    style={{ backgroundColor: theme.accent }}
                  >
                    <Icon className="h-4 w-4" strokeWidth={2.5} />
                  </span>
                  <span className="text-white font-bold">{business}</span>
                </div>
                <div>
                  <h3
                    id="demo-modal-title"
                    className="text-white text-2xl md:text-4xl font-bold tracking-tight leading-tight max-w-xl"
                  >
                    {tagline}
                  </h3>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <span
                      className="inline-flex items-center rounded-lg px-4 py-2 text-sm font-semibold text-white"
                      style={{ backgroundColor: theme.accent }}
                    >
                      {primaryCta}
                    </span>
                    <span className="inline-flex items-center rounded-lg px-4 py-2 text-sm font-semibold text-white/95 border border-white/40">
                      Call Now
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="text-sm font-semibold uppercase tracking-widest text-goc-muted">
                Services
              </h4>
              <ul className="mt-3 grid grid-cols-2 gap-2">
                {services.map((s) => (
                  <li
                    key={s}
                    className="rounded-lg border border-goc-border bg-white px-3 py-2 text-sm text-goc-text"
                  >
                    {s}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold uppercase tracking-widest text-goc-muted">
                Why Choose Us
              </h4>
              <ul className="mt-3 space-y-2">
                {tiles.map((t) => (
                  <li
                    key={t}
                    className="flex items-center gap-2 rounded-lg bg-goc-light px-3 py-2 text-sm text-goc-text"
                  >
                    <span
                      className="inline-flex h-2 w-2 rounded-full"
                      style={{ backgroundColor: theme.accent }}
                    />
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <p className="mt-6 text-xs text-goc-muted">
            This preview is for illustration only. Demo — not a real customer.
          </p>
        </div>
      </div>
    </div>
  );
};

export const DemoWebsites = () => {
  const [active, setActive] = useState(null);

  return (
    <section
      id="demos"
      className="bg-white"
      data-testid="section-demo-websites"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-24">
        <SectionHeading
          eyebrow="Examples"
          title="Website Examples"
          subtitle="See the types of professional small-business websites we can create."
          testId="heading-demo-websites"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {DEMOS.map((d) => (
            <DemoCard key={d.key} demo={d} onOpen={() => setActive(d)} />
          ))}
        </div>
      </div>
      <DemoModal demo={active} onClose={() => setActive(null)} />
    </section>
  );
};

export default DemoWebsites;
